import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/store/auth";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, Users, Building2, ListChecks, Repeat,
  CheckSquare, MessageSquare, Brain, BarChart3, Settings, LogOut, FileText,
  Search, Shield, ListTree, Tags, CalendarDays, AlertTriangle, Hash,
  ScrollText, Bot, Send, Inbox, GitBranch,
} from "lucide-react";

const NAV = [
  { label: "Dashboard", short: "Home", icon: LayoutDashboard, to: "/", exact: true },
  {
    label: "Tasks", short: "Tasks", icon: ListChecks, items: [
      { to: "/tasks",     label: "Tasks",           icon: ListChecks },
      { to: "/recurring", label: "Recurring Tasks", icon: Repeat },
    ],
  },
  { label: "Pending Approvals", short: "Approve", icon: CheckSquare, to: "/approvals" },
  {
    label: "Masters", short: "Masters", icon: Users, items: [
      { to: "/users",             label: "Users",              icon: Users },
      { to: "/roles",             label: "Roles & Permissions", icon: Shield },
      { to: "/departments",       label: "Departments",        icon: Building2 },
      { to: "/task-types",        label: "Task Types",         icon: ListTree },
      { to: "/priorities",        label: "Priorities",         icon: Tags },
      { to: "/holidays",          label: "Holiday Calendar",   icon: CalendarDays },
      { to: "/escalation-rules",  label: "Escalation Rules",   icon: AlertTriangle },
      { to: "/numbering-series",  label: "Numbering Series",   icon: Hash },
      { to: "/workflows",         label: "Approval Workflows", icon: GitBranch },
    ],
  },
  {
    label: "WhatsApp", short: "WhatsApp", icon: MessageSquare, items: [
      { to: "/whatsapp/templates", label: "Templates",          icon: FileText },
      { to: "/whatsapp/incoming",  label: "Incoming Messages",  icon: Inbox },
      { to: "/whatsapp/outgoing",  label: "Outgoing Messages",  icon: Send },
    ],
  },
  {
    label: "AI", short: "AI", icon: Brain, items: [
      { to: "/memory",      label: "Memory Search", icon: Brain },
      { to: "/memory/chat", label: "AI Chat",       icon: Bot },
    ],
  },
  {
    label: "Reports", short: "Reports", icon: BarChart3, items: [
      { to: "/reports",          label: "SKIVERSE Reports",  icon: BarChart3 },
      { to: "/user-performance", label: "User Performance",  icon: Users },
      { to: "/audit",            label: "Audit Logs",        icon: ScrollText },
    ],
  },
  { label: "Settings", short: "Settings", icon: Settings, to: "/settings" },
];

function matchesGroup(group, pathname) {
  if (group.items) {
    return group.items.some((it) => pathname === it.to || pathname.startsWith(it.to + "/"));
  }
  if (group.exact) return pathname === group.to;
  return pathname === group.to || pathname.startsWith(group.to + "/");
}

function findActiveGroup(pathname) {
  return NAV.find((g) => matchesGroup(g, pathname)) || NAV[0];
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const active = findActiveGroup(location.pathname);
  const items = active?.items || [];

  return (
    <div className="app">
      {/* Left app-switcher rail */}
      <aside className="rail" data-testid="rail">
        <div className="rail-items">
          {NAV.map((g) => {
            const Icon = g.icon;
            const isActive = g.label === active?.label;
            const target = g.to || (g.items && g.items[0].to);
            return (
              <button
                key={g.label}
                className={`rail-item ${isActive ? "active" : ""}`}
                onClick={() => navigate(target)}
                title={g.label}
                data-testid={`rail-${g.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={21} strokeWidth={1.8} />
                <span>{g.short}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <div className="shell">
        {/* Row 1 — brand + search + user */}
        <header className="topbar" data-testid="topbar">
          <div className="brand">
            <img
              className="brand-logo"
              src="https://skiverse.skicorp.in/upload/process/79871716008647color_logo_without_tagline.png"
              alt="Skiverse"
            />
            <div className="brand-divider" />
            <div className="brand-sub">
              <span>Task</span><span>Management</span><span>System</span>
            </div>
          </div>

          <div className="shell-search hidden md:block">
            <span className="ico"><Search size={15} /></span>
            <input placeholder="Search tasks, users, memories..." data-testid="global-search" />
          </div>

          <div className="flex items-center gap-2" style={{ marginLeft: "auto" }}>
            <NotificationBell />
            <div className="user-chip">
              <div className="avatar">{user?.name?.[0]?.toUpperCase() || "U"}</div>
              <div className="hidden lg:flex flex-col leading-tight">
                <span className="uname">{user?.name}</span>
                <span className="urole">{user?.role_name || user?.role_code}</span>
              </div>
              <button
                className="logout-btn"
                onClick={async () => { await logout(); navigate("/login"); }}
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut size={15} /><span className="lo-label">Sign out</span>
              </button>
            </div>
          </div>
        </header>

        {/* Row 2 — active section's horizontal nav */}
        {items.length > 0 && (
          <nav className="topnav" data-testid="topnav">
            {items.map((it) => {
              const ItIcon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                >
                  <ItIcon size={15} strokeWidth={1.8} />
                  <span>{it.label}</span>
                </NavLink>
              );
            })}
          </nav>
        )}

        <main className="shell-content fade-in" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
